import _ from 'lodash';
import store from 'store/index';
import moment from 'moment';
import Api from "services/Api";
import Validator from 'services/Validator';
import {areAllTrue} from 'services/helper';
import {validDate} from 'components/form/validator/validations';
import {defaultIncomePeriodTemplate, personTemplate} from 'store/modules/shared/templates';
import {
    determineLife,
    doesIncomePeriodNeedToUpdate,
    dynamicUpdates,
    findNextPeriodEnd,
    findNextPeriodStart,
    incomeValid,
    isValidIncome,
    removePeriodsSplitForTheRetired,
    splitPeriodsForRetirement,
    throttledIncome,
    updateIncomePeriodsForRetirement,
    updateObject,
    userSettable,
    zeroCheck
} from 'services/personFunctions';
import {previousValid} from 'store/generators/step';

export const mutationTypes = {
    RESET: 'RESET',
    ADD_DEPENDANT: 'ADD_DEPENDANT',
    UPDATE_DEPENDANT: 'UPDATE_DEPENDANT',
    DELETE_DEPENDANT: 'DELETE_DEPENDANT',
    UPDATE_DEPENDANT_INCOME: 'UPDATE_DEPENDANT_INCOME',
    SERVER_UPDATE_DEPENDANT: 'SERVER_UPDATE_DEPENDANT',
    ADD_DEPENDANT_INCOME_PERIOD: 'ADD_DEPENDANT_INCOME_PERIOD',
    UPDATE_DEPENDANT_INCOME_PERIOD: 'UPDATE_DEPENDANT_INCOME_PERIOD',
    DELETE_DEPENDANT_INCOME_PERIOD: 'DELETE_DEPENDANT_INCOME_PERIOD',
    SERVER_UPDATE_DEPENDANTS_INCOME: 'SERVER_UPDATE_DEPENDANTS_INCOME',
    UPDATE_DEPENDANT_LIFE_EXPECTANCY: 'UPDATE_DEPENDANT_LIFE_EXPECTANCY',
    UPDATE_DEPENDENCY_ON_INCOME_VALID: 'UPDATE_DEPENDENCY_ON_INCOME_VALID',
    UPDATING_DEPENDANT_INCOME_PERIOD: 'UPDATING_DEPENDANT_INCOME_PERIOD'
};

// Initial State
const state = {
    theDependants: [],
    dependencyOnIncomeValid: false,
    updatingDependantIncome: false,
};

/**
 * Validation Rules.
 *
 * @type {Object}
 */
const rules = {
    'theDependants': {type: 'array', rules: 'required'},
};

/**
 * Determine the length of a dependency compared to deceased,
 * life expectancy or if the person is under specified age.
 *
 * @param state
 * @param rootGetters
 * @param {number} index dependant to compare to
 * @return {{duration:number,date:string, type:string}}
 */
const determineDependency = (state) => (index) => {
    if (!state.theDependants[index]) {
        return {};
    }

    const deceasedDateOfDeath = store.getters['fatal/dependency/deceased/theDeceased']['dateOfDeath'];
    const deceasedButForDate = store.getters['fatal/dependency/deceased/theDeceasedLifeExpectancy']['dateOfDeath'];
    const assumedSettlement = store.getters['fatal/dependency/claimDetails/value']('assumedSettlement');
    const dependantDateOfDeath = state.theDependants[index].lifeExpectancy.dateOfDeath;

    const mDeceasedDateOfDeath = moment(deceasedDateOfDeath, 'DD/MM/YYYY');
    const mDeceasedButForDate = moment(deceasedButForDate, 'DD/MM/YYYY');
    const mAssumedSettlement = moment(assumedSettlement, 'DD/MM/YYYY');
    const mDependantDob = moment(state.theDependants[index].dateOfBirth, 'DD/MM/YYYY');
    const mDependantDateOfDeath = moment(dependantDateOfDeath, 'DD/MM/YYYY');

    // Is Assuemd Settlement Valid.
    if (!validDate(assumedSettlement)) {
        return {};
    }

    // // If the claimant is under specified dependency age
    if (Math.abs(mDeceasedDateOfDeath.diff(mDependantDob, 'years')) < 21) {
        let dependencyEndAge = 18;
        switch (state.theDependants[index].lifeExpectancy.method) {
            case 'dependent_until_custom':
                dependencyEndAge = state.theDependants[index].lifeExpectancy.child.youthCutOffAge;
                break;
            case 'dependent_until_16':
                dependencyEndAge = 16;
                break;
            case 'dependent_until_21':
                dependencyEndAge = 21;
                break;
        }

        const dependencyEnd = mDependantDob.add(dependencyEndAge, 'years');

        if (mDeceasedDateOfDeath.isAfter(dependencyEnd) || mDeceasedButForDate.isAfter(dependencyEndAge)) {
            return {
                duration: dependencyEnd.diff(mDeceasedDateOfDeath, 'years', true),
                date: dependencyEnd.format('DD/MM/YYYY'),
                type: `To dependant's age of ${dependencyEndAge}`,
            };
        }
    }

    // Is Dependant Date of Death Valid.
    if (!validDate(dependantDateOfDeath)) {
        return {};
    }

    if (mDeceasedDateOfDeath.isAfter(mDependantDateOfDeath) || mDeceasedButForDate.isAfter(mDependantDateOfDeath)) {
        return {
            duration: mDependantDateOfDeath.diff(mDeceasedDateOfDeath, 'years', true),
            date: mDependantDateOfDeath.format('DD/MM/YYYY'),
            type: "To end of dependant's life expectancy",
        };
    }

    return {
        duration: mDeceasedButForDate.diff(mDeceasedDateOfDeath, 'years', true),
        date: mDeceasedButForDate.format('DD/MM/YYYY'),
        type: "To end of the deceased's life expectancy",
    };
};

/**
 * Define the update actions in a functional way to digest for dependant
 * Curried function configured for when deceased values change
 *
 * @type {function(key, dispatch, person, index):Array.<Promise>}
 */
const dependantUpdateEvents = dynamicUpdates({
    lifeExpectancy: 'updateDependantLifeExpectancy',
    incomeValue: 'updateDependantIncomeValue',
    retirementDate: 'updateDependantRetirementDate',
});

/**
 * Calculate pre trial dependency
 *
 * @param state
 */
const getPreTrialDependency = () => {
    const dateOfDeath = store.getters['fatal/dependency/deceased/theDeceased']['dateOfDeath'];
    return moment(store.getters['fatal/dependency/claimDetails/value']('assumedSettlement'), 'DD/MM/YYYY').diff(
        moment(dateOfDeath, 'DD/MM/YYYY'), 'years', true,
    );
};

/**
 * Calculate split periods
 *
 * @param state
 */
const getHasSplitPeriods = (state) => () => {
    return (!store.getters['fatal/dependency/deceased/theDeceased']['income']['unchangedPension']
        || !store.getters['fatal/dependency/deceased/theDeceased']['income']['retired']) ?
        true : state.theDependants.reduce((hasSplit, dependant) => hasSplit
            || !dependant.income.unchangedPension
            || !dependant.income.retired, false);
};

/**
 * Throttle update of person life expectancy to allow for typing
 *
 * @param dispatch
 * @param action
 * @param person
 * @param lifeExpectancy
 * @param index
 */
export const throttleUpdateLife = _.debounce((dispatch, action, person, lifeExpectancy, index) => {
    if (validDate(person.dateOfBirth)) {
        let assumedSettlement = store.getters['fatal/dependency/claimDetails/value']('assumedSettlement');
        determineLife(assumedSettlement, person, lifeExpectancy)
            .then(updatedLife => updateObject(dispatch, action, updatedLife, index));
        //.then(() => dispatch('updatePeriodsForLifeExpectancy'));
    }
}, 500);

const valueFromState = (state, key) => state[key];
/**
 * is a dependant valid?
 * Do they have a name, dob, gender and life expectancy
 *
 * @param rootState
 */
const dependantValid = rootState => dependant => {
    return (rootState.user.user.hideNames || !!dependant.name)
        && (!!dependant.dateOfBirth)
        && (!!dependant.gender);
};
// Getters
const getters = {
    dependants: state => state.theDependants,
    allDependantsValid: (state, rootGetters, rootState) => areAllTrue(state.theDependants, dependantValid(rootState)),
    findDependant: state => (index) => state.theDependants[index],
    theDependant: state => index => valueFromState(state.theDependants, index),
    durationOfDependency: (state) => determineDependency(state),
    preTrialDependency: state => getPreTrialDependency(),
    dependantIncomeValid: state => index => {
        let assumedSettlement = moment(store.getters['fatal/dependency/proceduralDates/date']('dateOfAssumedSettlement'), 'DD/MM/YYYY');
        return incomeValid(assumedSettlement, state.theDependants[index])
    },
    allDependantsIncomeValid: state => {
        let assumedSettlement = moment(store.getters['fatal/dependency/proceduralDates/date']('dateOfAssumedSettlement'), 'DD/MM/YYYY');
        return state.theDependants.reduce((valid, value) => !valid ? valid : incomeValid(assumedSettlement, value), true);
    },
    hasSplitPeriods: (state) => getHasSplitPeriods(state),
    getNoOfDependantPeriods: state => (index) => state.theDependants[index].income.periods.length,
    getDependantIncomePeriod: state => (dependant, index) => state.theDependants[dependant].income.periods[index],
    getDependencyOnIncomeValid: state => state.dependencyOnIncomeValid,
    dependantNetIncome: state => state.theDependants.reduce((total, dependant) => total + dependant.income.netIncome + dependant.income.netPensionIncome, 0),
    getDependantIncomeStatus: state => state.updatingDependantIncome
};

// Actions
const actions = {
    /**
     * Save the step by submitting to the server.
     *
     * @param dispatch
     * @param payload
     */
    save({dispatch}, payload) {
        // We might change this to something whichf
        // is a bit cleaner...
        payload.data = state;

        Api.claims.save(payload).then((response) => {
            return response;
        });
    },

    /**
     * Loads the step being received from the Server, to be placed in the state.
     *
     * @param commit
     * @param step
     */
    load({commit}, step) {
        const data = _.deeply(_.mapKeys)(step, (value, key) => _.camelCase(key)).data;

        data.theDependants.map((dependant, index) =>
            commit(mutationTypes.SERVER_UPDATE_DEPENDANT, {
                dependant,
                index
            }));
    },

    /**
     * Performs any processing once the data from the server has been stored.
     * This is a good place to do calculations or dispatch other actions which
     * are used to refresh/update calculated data on other steps/components.
     */
    process({rootState}) {
        store.dispatch('fatal/dependency/step/setStepNumberValid', {
            stepNo: 2,
            valid: previousValid(rootState, 2) && (new Validator(state, rules)).validate(),
        });
    },

    /**
     * Validate the step.
     *
     * @param commit
     */
    validate({commit, rootState}) {
        store.dispatch('fatal/dependency/step/setStepNumberValid', {
            stepNo: 2,
            valid: previousValid(rootState, 2) && (new Validator(state, rules)).validate(),
        });
    },

    /**
     * Resets the step back to defaults
     *
     * @param commit
     */
    reset({commit}) {
        store.dispatch('fatal/dependency/step/setStepNumberValid', {
            stepNo: 2,
            valid: false,
        });

        commit(mutationTypes.RESET);
    },

    /**
     * Add a new dependant
     *
     * @param commit
     */
    addDependant({commit}) {
        commit(mutationTypes.ADD_DEPENDANT);
    },

    /**
     * Delete a dependant
     *
     * @param commit
     * @param state
     * @param dispatch
     * @param index
     */
    deleteDependant({commit, state, dispatch}, index) {
        commit(mutationTypes.DELETE_DEPENDANT, index);
        if (!state.theDependants.length) {
            return dispatch('addDependant');
        }
    },

    /**
     * Update a dependant
     *
     * @param dispatch
     * @param index
     * @param dependant
     */
    updateDependant({dispatch}, {index, dependant}) {
        return updateObject(dispatch, 'updateDependantValue', dependant, index).then(() => {
            store.dispatch('fatal/dependency/checkToUpdateMultipliers')
        });
    },

    /**
     * Update the dependant value
     *
     * @param dispatch
     * @param commit
     * @param state
     * @param key
     * @param value
     * @param index
     */
    updateDependantValue({commit, state, dispatch}, {key, value, index}) {
        if (state.theDependants[index][key] !== value && userSettable(key)) {
            commit(mutationTypes.UPDATE_DEPENDANT, {key, value, index});
            // Update other values that rely on dependant values
            return Promise.all(dependantUpdateEvents(key, dispatch, state.theDependants[index], index));
        }
    },

    /**
     * Update the dependant's LifeExpectancy
     *
     * @param dispatch
     * @param state
     * @param index
     * @param lifeExpectancy
     */
    updateDependantLifeExpectancy({dispatch, state}, {index, lifeExpectancy}) {
        return updateObject(dispatch, 'updateDependantLifeExpectancyValue', lifeExpectancy, index)
            .then(() => throttleUpdateLife(
                dispatch,
                'updateDependantLifeExpectancyValue',
                state.theDependants[index],
                lifeExpectancy,
                index),
            );
    },

    /**
     * Update the deceased LifeExpectancy store
     *
     * @param state
     * @param commit
     * @param index
     * @param key
     * @param value
     */
    updateDependantLifeExpectancyValue({commit, state}, {key, value, index}) {
        if (state.theDependants[index].lifeExpectancy[key] !== value) {
            commit(mutationTypes.UPDATE_DEPENDANT_LIFE_EXPECTANCY, {key, value, index});
        }
    },

    /**
     * Update the deceased LifeExpectancy store
     *
     * @param state
     * @param commit
     * @param index
     */
    addDependantIncomePeriod({commit, state}, {index}) {
        let person = state.theDependants[index];
        let periods = person.income.periods;

        commit(mutationTypes.ADD_DEPENDANT_INCOME_PERIOD,
            {
                dependant: index,
                period: defaultIncomePeriodTemplate({
                    start: findNextPeriodStart(periods, store.getters['fatal/dependency/deceased/theDeceased']['dateOfDeath']),
                    end: findNextPeriodEnd(periods, store.getters['fatal/dependency/deceased/theDeceasedLifeExpectancy']['dateOfDeath']),
                })
            });
    },

    /**
     * Update the dependant's income period
     *
     * @param dispatch
     * @param state
     * @param index
     * @param period
     * @param dependant
     */
    updateDependantIncomePeriod({commit, dispatch, state}, {index, period, dependant, dependantIndex}) {
        if (!state.updatingDependantIncome) {
            commit(mutationTypes.UPDATING_DEPENDANT_INCOME_PERIOD);
        }

        let proms = Object.keys(period)
            .map(key => dispatch('updateDependantIncomePeriodValue', {
                key,
                value: period[key],
                index,
                dependant,
                dependantIndex
            }));
        Promise.all(proms)
            .then(updatedKeys => {
                if (!doesIncomePeriodNeedToUpdate(updatedKeys) || !isValidIncome(period)) {
                    return;
                }

                if (dependant.income.retirementAge < 50
                    || dependant.income.retirementAge === ""
                    || dependant.income.retirementDate === null
                ) {
                    return;
                }

                return Promise.all([
                    zeroCheck(dispatch, index, period, dependant, 'updateDependantIncomePeriodValue', 'dependant').then(() => {
                        dispatch('fatal/dependency/dependencyOnIncome/resetMultiplier', null, {root: true});
                    }),

                    // The fourth argument of throttledIncome (`args`) passes the arguments
                    // required by updateDependantIncomePeriodValues. This isn't very
                    // clean as we have duplicate and nested paramaters. Refactor
                    // required: https://bclegal.atlassian.net/browse/QT-1039
                    throttledIncome(period, dispatch, 'updateDependantIncomePeriodValue', {
                        index,
                        deceased: store.getters['fatal/dependency/deceased/theDeceased'],
                        dependant: state.theDependants[dependantIndex],
                        dependantIndex
                    }, state.theDependants[dependantIndex])
                ]);
            }).then(() => {
            if (state.updatingDependantIncome) {
                commit(mutationTypes.UPDATING_DEPENDANT_INCOME_PERIOD);
            }
        })
    },

    /**
     * Update the dependant's income period values
     *
     * @param commit
     * @param index
     * @param key
     * @param value
     * @param dependant
     */
    updateDependantIncomePeriodValue({commit}, {index, key, value, dependant, dependantIndex}) {
        if (typeof dependant.income.periods[index] !== 'undefined') {
            let currentPeriod = dependant.income.periods[index];
            if (currentPeriod[key] === value) {
                return;
            }

            commit(mutationTypes.UPDATE_DEPENDANT_INCOME_PERIOD, {index, key, value, dependant, dependantIndex});
            return key;
        }
    },

    /**
     * Delete a dependant's income period
     *
     * @param commit
     * @param dispatch
     * @param dependant
     * @param index
     */
    deleteDependantIncomePeriod({commit, dispatch}, {dependant, dependantIndex, index}) {
        let periods = dependant.income.periods;
        let endOfPeriod = periods[index].endOfPeriod;
        commit(mutationTypes.DELETE_DEPENDANT_INCOME_PERIOD, {dependantIndex, index});
        dispatch('fatal/dependency/updateTheMultiplicand', null, {root: true});

        if (!periods.length) {
            return dispatch('addDependantIncomePeriod', {index: dependantIndex});
        }
        if (periods[index - 1]) {
            return dispatch('updateDependantIncomePeriodValue', {
                index: index - 1,
                key: 'endOfPeriod',
                value: endOfPeriod,
                dependant,
                dependantIndex
            });
        }

    },

    /**
     * Update a dependant's income
     *
     * @param dispatch
     * @param income
     * @param index
     */
    updateDependantIncome({dispatch}, {index, income}) {
        let previousRetirementDate = state.theDependants[index].income.retirementDate;
        // Resolve all changed values
        return updateObject(dispatch, 'updateDependantIncomeValue', income, index).then(keys => {
            if (~keys.indexOf('retired') && income.retired) {
                return dispatch('removePeriodsSplitForTheRetired', {previousRetirementDate, index})
                    .then(() => dispatch('updateIncomePeriodsForRetirement', index));
            }
            if (~keys.indexOf('retirementDate') || (~keys.indexOf('retired') && !income.retired)) {
                return dispatch('updateIncomePeriodForRetirementChange', {previousRetirementDate, index});
            }
        });
    },

    /**
     * Update the retirement date
     *
     * @param state
     * @param commit
     */
    updateDependantRetirementAgeAndDate: _.debounce(({state, commit, dispatch}, {index}) => {
        let theDependant = state.theDependants[index];
        let previousRetirementDate = theDependant.income.retirementDate;

        if (theDependant.income.retirementAgeCalc === 'auto') {
            return Api.calculators.dates.statePensionAge(theDependant)
                .then((data) => {
                    commit(mutationTypes.UPDATE_DEPENDANT_INCOME, {
                        key: 'retirementAge',
                        value: data.statePension.retirementAge,
                        index
                    });
                    commit(mutationTypes.UPDATE_DEPENDANT_INCOME, {
                        key: 'retirementDate',
                        value: data.statePension.retirementDate,
                        index
                    });
                    return dispatch('updateIncomePeriodForRetirementChange', {previousRetirementDate, index});

                });
        }
    }, 500),

    /**
     * Update the retirement date
     *
     * @param state
     * @param commit
     * @param index
     * @param key
     * @param value
     */
    updateDependantIncomeValue({commit, state}, {key, value, index}) {
        if (state.theDependants[index].income[key] === value || (typeof value === "number" && isNaN(value))) {
            return;
        }
        commit(mutationTypes.UPDATE_DEPENDANT_INCOME, {key, value, index});
        return key;
    },

    /**
     * Remove a dependant's invalid income period
     *
     * @param index
     * @param state
     * @param dispatch
     */
    removeDependantInvalidPeriodAndAddDefaults({dispatch, state, rootGetters}, {dependantIndex}) {
        let dateOfDeath = rootGetters['fatal/dependency/deceased/theDeceased'].dateOfDeath;


        // Remove the invalid periods
        state.theDependants[dependantIndex].income.periods.forEach((period, index) => {
            if (state.theDependants[dependantIndex].income.retired) {
                dispatch('updateDependantIncomePeriodValue', {
                    key: 'employmentIncome',
                    value: 0,
                    index,
                    dependant: state.theDependants[dependantIndex],
                    dependantIndex
                });

                dispatch('fatal/dependency/dependencyOnIncome/resetMultiplier', null, {root: true});
                throttledIncome(period, dispatch, 'updateDependantIncomePeriodValue', {
                    index,
                    dependant: state.theDependants[dependantIndex]
                }, store.getters['fatal/dependency/deceased/theDeceased'], state.theDependants[dependantIndex], dependantIndex);
            }

            if (moment(period.startOfPeriod, 'DD/MM/YYYY').isBefore(moment(dateOfDeath, 'DD/MM/YYYY'))) {
                dispatch('deleteDependantIncomePeriod', {
                    index,
                    dependant: state.theDependants[dependantIndex],
                    dependantIndex
                })
            }
        });
    },

    /**
     * Update the validity of the dependency on income
     *
     * @param commit
     * @param valid
     */
    updateDependencyOnIncomeValid({commit}, valid) {
        commit(mutationTypes.UPDATE_DEPENDENCY_ON_INCOME_VALID, valid);
    },

    /**
     * Calculate duration for dependant
     *
     * @param getters
     * @param index
     */
    calcDurationForDependant({getters}, index) {
        return getters.durationOfDependency(index);
    },

    /**
     * Update dependants income from server
     *
     * @param commit
     * @param data
     */
    serverUpdateDependantsIncome({commit}, {data}) {
        commit(mutationTypes.SERVER_UPDATE_DEPENDANTS_INCOME, data.dependants);
    },

    /**
     * Update income periods for the dependants when the deceased DOD is changed
     *
     * @param state
     * @param commit
     * @param dateOfDeath
     */
    updatePeriodsForLifeExpectancy({dispatch, state}, dateOfDeath) {
        let mDateOfDeath = moment(dateOfDeath, 'DD/MM/YYYY');

        let proms = [];
        state.theDependants.forEach((dependant, dependantIndex) => {
            let lastIndex = dependant.income.periods.length - 1;
            proms.concat(dependant.income.periods.map((period, index) => {
                let start = moment(period.startOfPeriod, 'DD/MM/YYYY');
                let end = moment(period.endOfPeriod, 'DD/MM/YYYY');
                // If it is now after the date of death
                if (start.isAfter(mDateOfDeath)) {
                    // Remove it
                    return dispatch('deleteDependantIncomePeriod', {dependant, dependantIndex, index});
                }
                // If it is the last period or end after date of death, shift it back.
                else if (end.isAfter(mDateOfDeath) || index === lastIndex) {
                    return dispatch('updateDependantIncomePeriodValue', {
                        index,
                        key: 'endOfPeriod',
                        value: dateOfDeath,
                        dependant,
                        dependantIndex
                    })
                }
            }));
        });
        return Promise.all(proms);
    },
    /**
     * Split current periods for retirement age or maintain periods that are already split.
     *
     * @param state
     * @param commit
     * @param previousRetirementDate
     * @param index
     */
    updateIncomePeriodForRetirementChange({state, commit}, {previousRetirementDate, index}) {
        let dependant = state.theDependants[index];
        let retirementDate = dependant.income.retirementDate;
        let periods = dependant.income.periods;
        let end = periods[periods.length - 1].endOfPeriod;
        splitPeriodsForRetirement(
            commit,
            mutationTypes.UPDATE_DEPENDANT_INCOME_PERIOD,
            mutationTypes.ADD_DEPENDANT_INCOME_PERIOD,
            previousRetirementDate,
            retirementDate,
            periods,
            end,
            dependant,
            index,
        );
    },

    /**
     * Remove an periods split for retirement.
     *
     * @param dispatch
     * @param state
     * @param previousRetirementDate
     * @param index
     * @return {Promise}
     */
    removePeriodsSplitForTheRetired({dispatch, state}, {previousRetirementDate, index}) {
        let dependant = state.theDependants[index];
        let retirementDate = dependant.income.retirementDate;
        let periods = dependant.income.periods;
        if (!dependant.income.retired) {
            return;
        }
        return removePeriodsSplitForTheRetired(
            dispatch,
            'deleteDependantIncomePeriod',
            periods,
            previousRetirementDate,
            retirementDate,
            {
                dependant,
                dependantIndex: index
            }
        );

    },

    updateIncomePeriodsForRetirement({state, dispatch}, dependantIndex) {
        let dependant = state.theDependants[dependantIndex];
        let periods = dependant.income.periods;
        return updateIncomePeriodsForRetirement(
            dispatch,
            'updateDependantIncomePeriodValue',
            periods,
            dependant.income.retired,
            {dependant, dependantIndex}
        ).then((updatedPeriodIndex) => {
            let proms = updatedPeriodIndex.map((updated, index) => {
                if (!updated) return;
                return zeroCheck(dispatch, index, periods[index], dependant, 'updateDependantIncomePeriodValue', 'dependant')
                    .then(() => throttledIncome(periods[index], dispatch, 'updateDependantIncomePeriodValue', {
                        index,
                        deceased: store.getters['fatal/dependency/deceased/theDeceased'],
                        dependant,
                        dependantIndex
                    }, state.theDependants[dependantIndex]));
            });
            return Promise.all(proms);
        })
    },
};

// Mutations
const mutations = {
    [mutationTypes.ADD_DEPENDANT](state) {
        const dependant = personTemplate();
        dependant.index = state.theDependants.length;
        state.theDependants.push(dependant);
    },
    [mutationTypes.UPDATE_DEPENDANT](state, {key, value, index}) {
        state.theDependants[index][key] = value;
    },
    [mutationTypes.SERVER_UPDATE_DEPENDANTS_INCOME](state, data) {
        state.theDependants.map(function (dependant, key) {
                dependant.income = data[key];
            }
        )
    },
    [mutationTypes.UPDATE_DEPENDANT_LIFE_EXPECTANCY](state, {key, value, index}) {
        state.theDependants[index].lifeExpectancy[key] = value;
    },
    [mutationTypes.DELETE_DEPENDANT](state, index) {
        state.theDependants.splice(index, 1);
    },
    [mutationTypes.ADD_DEPENDANT_INCOME_PERIOD](state, {dependant, period}) {
        state.theDependants[dependant].income.periods.push(period);
        state.theDependants[dependant].income.netPensionIncome += period.netPensionIncome;
        state.theDependants[dependant].income.grossPensionIncome += period.grossPensionIncome;
        state.theDependants[dependant].income.netIncome += period.netIncome;
        state.theDependants[dependant].income.grossIncome += period.grossIncome;
    },
    [mutationTypes.UPDATE_DEPENDANT_INCOME_PERIOD](state, {dependant, key, index, value, dependantIndex}) {
        let income = dependant.income;

        if (~key.indexOf('Income')) {
            income[key] -= (income.periods[index][key] || 0);
        }
        income.periods[index][key] = value;
        if (~key.indexOf('Income')) {
            income[key] += (value || 0);
        }
    },
    [mutationTypes.DELETE_DEPENDANT_INCOME_PERIOD](state, {dependantIndex, index}) {
        state.theDependants[dependantIndex].income.periods.splice(index, 1);
    },
    [mutationTypes.UPDATE_DEPENDENCY_ON_INCOME_VALID](state, valid) {
        state.dependencyOnIncomeValid = valid;
    },
    [mutationTypes.UPDATE_DEPENDANT_INCOME](state, {key, value, index}) {
        state.theDependants[index].income[key] = value;
    },
    [mutationTypes.RESET](state) {
        state.theDependants = [];
    },
    [mutationTypes.SERVER_UPDATE_DEPENDANT](state, {dependant, index}) {
        state.theDependants.push(personTemplate());
        state.theDependants[index].id = dependant.id;
        state.theDependants[index].name = dependant.name;
        state.theDependants[index].gender = dependant.gender;
        state.theDependants[index].dateOfBirth = dependant.dateOfBirth;
        state.theDependants[index].lifeExpectancy = dependant.lifeExpectancy;
        state.theDependants[index].income = dependant.income;
    },
    [mutationTypes.UPDATING_DEPENDANT_INCOME_PERIOD](state) {
        state.updatingDependantIncome = !state.updatingDependantIncome;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/fatal/dependency/dependants.js